import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

// Shared visual shell for /login, /signup, and onboarding/auth-status pages.
// Presentation-only: pages keep their existing route, auth, and API behavior.
export function AuthCard({
  title,
  subtitle,
  children,
  footer,
  eyebrow = 'Communication Record',
  aside,
  steps,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  eyebrow?: string;
  aside?: ReactNode;
  steps?: string[];
}) {
  return (
    <main className="min-h-screen bg-[#f5f3ee] px-6 py-10 text-slate-950">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-5xl items-center justify-center">
        <div className="grid w-full gap-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(380px,460px)] lg:items-center">
          <section className="hidden lg:block">
            <Link to="/" className="inline-flex items-center gap-3 text-slate-950 no-underline">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950 text-base font-bold text-white shadow-lg shadow-slate-300/80">
                O
              </span>
              <span>
                <span className="block text-lg font-semibold tracking-tight">Oracord</span>
                <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Communication Record
                </span>
              </span>
            </Link>
            <div className="mt-10 max-w-md rounded-[2rem] border border-white/70 bg-white/60 p-7 shadow-sm shadow-slate-200/80 backdrop-blur">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">
                Private by design
              </div>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950">
                Keep client conversations easier to find and follow up on.
              </h2>
              <p className="mt-4 text-sm leading-6 text-slate-600">
                Oracord is designed for communication records, call context, and follow-up clarity — without adding fake certainty to your customer conversations.
              </p>
              {aside && <div className="mt-5">{aside}</div>}
              {steps && steps.length > 0 && (
                <ol className="mt-6 space-y-3 text-sm text-slate-600">
                  {steps.map((step, index) => (
                    <li key={step} className="flex gap-3 rounded-2xl border border-slate-200/80 bg-white/70 p-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-800">
                        {index + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </section>

          <div className="w-full">
            <div className="mb-6 text-center lg:hidden">
              <Link to="/" className="text-lg font-semibold text-slate-950 no-underline">
                Oracord
              </Link>
              <div className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Communication Record
              </div>
            </div>
            <div className="rounded-[2rem] border border-white/80 bg-white/95 p-7 shadow-xl shadow-slate-300/40 backdrop-blur sm:p-8">
              {eyebrow && <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">{eyebrow}</div>}
              <h1 className="mt-3 text-2xl font-bold tracking-tight text-slate-950">{title}</h1>
              {subtitle && <p className="mt-2 text-sm leading-6 text-slate-600">{subtitle}</p>}
              <div className="mt-6">{children}</div>
            </div>
            {footer && <div className="mt-5 text-center text-sm text-slate-600">{footer}</div>}
          </div>
        </div>
      </div>
    </main>
  );
}

export function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-slate-700">{label}</span>
      <div className="mt-2">{children}</div>
      {error && <p className="mt-1.5 text-xs font-medium text-red-600">{error}</p>}
    </label>
  );
}

export const inputClass =
  'block w-full rounded-xl border border-slate-200 bg-[#fbfaf7] px-3.5 py-2.5 text-sm text-slate-950 shadow-sm shadow-slate-200/50 placeholder:text-slate-400 focus:border-amber-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-100';

export const primaryButtonClass =
  'w-full inline-flex justify-center rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-slate-300/80 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60';

export const secondaryButtonClass =
  'block rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-center text-sm font-semibold text-slate-700 shadow-sm shadow-slate-200/60 transition hover:border-amber-200 hover:bg-amber-50/50 no-underline';

const noticeToneClass = {
  amber: 'border-amber-200 bg-amber-50/90 text-amber-900',
  emerald: 'border-emerald-200 bg-emerald-50/90 text-emerald-900',
  slate: 'border-slate-200 bg-slate-50/90 text-slate-700',
} as const;

export function NoticeBanner({
  children,
  tone = 'slate',
  className = '',
}: {
  children: ReactNode;
  tone?: keyof typeof noticeToneClass;
  className?: string;
}) {
  return (
    <div className={`rounded-xl border px-3.5 py-3 text-sm leading-6 ${noticeToneClass[tone]} ${className}`.trim()}>
      {children}
    </div>
  );
}

export function ErrorBanner({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="mb-4 rounded-xl border border-red-200 bg-red-50/90 px-3.5 py-3 text-sm leading-6 text-red-800">
      {message}
    </div>
  );
}
